/**
 * useChatSessions Hook
 *
 * Manages the session list and CRUD operations (create, rename, delete).
 * Sessions are ordered by most recently updated.
 */

import { useState, useCallback } from 'react';
import {
  listSessions,
  createSession,
  deleteSession,
  renameSession,
} from '../services/chatbotApi';

/**
 * @returns {Object} session list state and actions
 */
export function useChatSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  /** Fetch all sessions for the authenticated user */
  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSessions();
      setSessions(Array.isArray(data) ? data : data?.sessions || []);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /** Create a new session and prepend it to the list */
  const addSession = useCallback(async () => {
    try {
      const session = await createSession();
      setSessions((prev) => [session, ...prev]);
      return session;
    } catch (err) {
      console.error('Failed to create session:', err);
      throw err;
    }
  }, []);

  /** Rename a session */
  const rename = useCallback(async (sessionId, title) => {
    try {
      await renameSession(sessionId, title);
      setSessions((prev) =>
        prev.map((s) =>
          (s.session_id || s.sessionId) === sessionId ? { ...s, title } : s,
        ),
      );
    } catch (err) {
      console.error('Failed to rename session:', err);
      throw err;
    }
  }, []);

  /** Delete a session and remove it from the list (optimistic) */
  const remove = useCallback(async (sessionId) => {
    // Remove from UI immediately
    setSessions((prev) =>
      prev.filter((s) => (s.session_id || s.sessionId) !== sessionId),
    );
    try {
      await deleteSession(sessionId);
    } catch (err) {
      // 404 = already gone, that's fine
      if (err.status !== 404) {
        console.error('Failed to delete session:', err);
        fetchSessions(); // re-fetch to restore if real error
      }
    }
  }, [fetchSessions]);

  return {
    sessions,
    loading,
    fetchSessions,
    addSession,
    rename,
    remove,
  };
}

export default useChatSessions;
